import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { Product } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useGetProduct } from 'src/services/product.service';
import ProductActionMenuButton from 'src/components/ActionButtons/ProductActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { loadData } from 'src/utils/grid.utils';

const ProductGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const { loading, products, fetchProducts, deleteProduct, totalItems } =
    useGetProduct();
  const [sortBy, setSortBy] = useState<string>('displayOrder');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'displayOrder', sort: 'asc' },
  ]);

  useEffect(() => {
    loadData(
      () => fetchProducts(page, pageSize, searchTerm, sortBy, order),
      showSnackbar
    );
  }, [state, pageSize, searchTerm, page, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    let sortBy = newSortModel[0]?.field || 'displayOrder';
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    if (sortBy === 'status') {
      sortBy = 'isActive';
    }
    setPage(1);
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'displayOrder',
      headerName: 'Display Order',
      flex: 0.5,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'unit',
      headerName: 'Unit',
      flex: 0.3,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'availableQty',
      headerName: 'Available Quantity',
      flex: 0.75,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'unitPrice',
      headerName: 'Unit Price',
      flex: 0.75,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <ProductActionMenuButton
          rowId={params.id}
          name={params.row.name}
          status={params.row.status}
          availableQty={params.row.availableQty}
          fetchProducts={() =>
            fetchProducts(page, pageSize, searchTerm, sortBy, order)
          }
          deleteProduct={deleteProduct}
        />
      ),
    },
  ];

  const transformData = (products: Product[]) => {
    try {
      return products.map((product, index) => ({
        id: product.id,
        number: (page - 1) * pageSize + index + 1,
        name: product.name,
        displayOrder: product.displayOrder,
        availableQty: new Intl.NumberFormat('en-US').format(
          Number(product.availableQty)
        ),
        unit: product.unit,
        unitPrice: new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(product.unitPrice)),
        status: product.isActive ? 'Active' : 'Inactive',
      }));
    } catch (error) {
      showSnackbar('Error transforming product data', 'error');
      return [];
    }
  };

  const rows = transformData(products);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default ProductGrid;
